import { getTenant } from "@/TENANT_CONFIGS";
import { useLocalStorage } from "@/utils/customHooks/useLocalStorage";
import { useEffect, useMemo, useRef } from "react";
import { flattenFilterConfig } from "./flattenFilterConfig";
import { useSessionStorage } from "@/utils/customHooks/useSessionStorage";

/**
 * useSavedFilterConfig
 *
 * Responsibilities:
 * - Persist a list of filter configuration objects (no values) in localStorage
 * - Persist active filters (with values) in sessionStorage
 * - Keep that list consistent with the current filterConfig structure
 *   - Drop configs whose filterId no longer exists in filterConfig
 *   - Add configs for filters that the user has interacted with (present in activeFilters)
 * - On mount, restore active filters from sessionStorage if available
 *
 * If setActiveFilters is provided, it will also clean up activeFilters to remove
 * any filters whose template/config no longer exists. This prevents sending stale
 * filter queries.
 */
export const getSavedFilterConfigKey = (tenant, module, storeKey) =>
  `savedFilterConfig_${tenant}_${module}${storeKey ? `_${storeKey}` : ""}`;

/**
 * Extracts unique parent-group combinations from filter values.
 * Used to persist the block grouping structure (breadcrumbs) so that
 * empty blocks can reconstruct their parent hierarchy when values are cleared.
 */
const extractGroupInfo = (values) => {
  if (!Array.isArray(values) || !values.length) return undefined;
  const hasParentInfo = values.some(
    (v) => v.parentThemeId || v.parentSubThemeId
  );
  if (!hasParentInfo) return undefined;
  const groups = new Map();
  for (const v of values) {
    const key = `${v.parentThemeId || "_"}::${v.parentSubThemeId || "_"}`;
    if (!groups.has(key)) {
      groups.set(key, {
        parentThemeId: v.parentThemeId || null,
        parentThemeDisplay: v.parentThemeDisplay || null,
        parentSubThemeId: v.parentSubThemeId || null,
        parentSubThemeDisplay: v.parentSubThemeDisplay || null,
      });
    }
  }
  return Array.from(groups.values());
};

const groupKey = (g) => `${g.parentThemeId || "_"}::${g.parentSubThemeId || "_"}`;

/**
 * Union of two group lists, keyed by parentThemeId::parentSubThemeId.
 * Existing entries take precedence to preserve display labels already saved.
 */
const mergeGroupInfo = (existing, incoming) => {
  if (!existing && !incoming) return undefined;
  const merged = new Map();
  for (const g of existing || []) merged.set(groupKey(g), g);
  for (const g of incoming || []) {
    const k = groupKey(g);
    if (!merged.has(k)) merged.set(k, g);
  }
  return merged.size > 0 ? Array.from(merged.values()) : undefined;
};

export const useSavedFilterConfig = ({
  module,
  activeFilters = [],
  filterConfig = [],
  setActiveFilters,
  storeKey,
  skipLocalFilterSave = false,
}) => {
  const tenant = getTenant();

  const savedFilterConfigKey = getSavedFilterConfigKey(tenant, module, storeKey);

  const [savedFilterConfig, setSavedFilterConfig] = useLocalStorage(
    savedFilterConfigKey,
    []
  );
  const [savedActiveFilters, setSavedActiveFilters] = useSessionStorage(
    savedFilterConfigKey,
    []
  );

  const hasRestoredRef = useRef(false);

  const flatFilterConfig = useMemo(
    () => flattenFilterConfig(filterConfig),
    [filterConfig]
  );

  // Build Map for O(1) lookups instead of using .find() in loops (js-index-maps)
  const filterConfigById = useMemo(
    () => new Map(flatFilterConfig.map((f) => [f.filterId, f])),
    [flatFilterConfig]
  );

  // Derive validFilterIds as a Set from the Map keys (rerender-derived-state-no-effect)
  const validFilterIds = useMemo(
    () => new Set(filterConfigById.keys()),
    [filterConfigById]
  );

  // Restore active filters from session storage on mount
  useEffect(() => {
    if (skipLocalFilterSave) return;
    if (hasRestoredRef.current) return;
    if (!flatFilterConfig.length) return;
    if (typeof setActiveFilters !== "function") return;

    hasRestoredRef.current = true;

    if (savedActiveFilters?.length > 0) {
      const validFilters = savedActiveFilters.filter((f) =>
        validFilterIds.has(f.filterId)
      );
      if (validFilters.length > 0) {
        setActiveFilters(validFilters);
      }
    }
  }, [
    skipLocalFilterSave,
    flatFilterConfig.length,
    savedActiveFilters,
    validFilterIds,
    setActiveFilters,
  ]);

  // Sync saved config and active filters
  useEffect(() => {
    if (skipLocalFilterSave) return;
    if (!flatFilterConfig.length) return;

    // Build a lookup of active filter values by filterId for group extraction
    const activeFilterMap = new Map();
    for (const f of activeFilters) {
      if (f?.filterId) activeFilterMap.set(f.filterId, f);
    }

    // Clean saved configs and collect existing IDs in a single iteration (js-combine-iterations)
    const cleanedSaved = [];
    const existingSavedIds = new Set();
    let groupsUpdated = false;

    for (const f of savedFilterConfig) {
      if (f?.filterId && validFilterIds.has(f.filterId)) {
        const activeFilter = activeFilterMap.get(f.filterId);
        const activeGroups = activeFilter
          ? extractGroupInfo(activeFilter.values)
          : undefined;

        // Union with existing _savedGroups so cleared buckets stay remembered
        // until the user explicitly removes them from saved config.
        const mergedGroups = mergeGroupInfo(f._savedGroups, activeGroups);

        if (
          mergedGroups &&
          JSON.stringify(mergedGroups) !== JSON.stringify(f._savedGroups)
        ) {
          cleanedSaved.push({ ...f, _savedGroups: mergedGroups });
          groupsUpdated = true;
        } else {
          cleanedSaved.push(f);
        }
        existingSavedIds.add(f.filterId);
      }
    }

    // Find new configs to add using O(1) Map lookup (js-index-maps)
    const newConfigs = [];
    for (const f of activeFilters) {
      const filterId = f?.filterId;
      if (filterId && !existingSavedIds.has(filterId)) {
        const config = filterConfigById.get(filterId);
        if (config) {
          const groups = extractGroupInfo(f.values);
          newConfigs.push(groups ? { ...config, _savedGroups: groups } : config);
        }
      }
    }

    const hasSavedConfigChanged =
      cleanedSaved.length !== savedFilterConfig.length ||
      newConfigs.length > 0 ||
      groupsUpdated;

    if (hasSavedConfigChanged) {
      setSavedFilterConfig([...cleanedSaved, ...newConfigs]);
    }

    // Sync active filters to session storage
    setSavedActiveFilters(activeFilters);

    // Clean up stale activeFilters
    if (typeof setActiveFilters === "function") {
      const cleanedActive = activeFilters.filter((f) =>
        validFilterIds.has(f.filterId)
      );
      if (cleanedActive.length !== activeFilters.length) {
        setActiveFilters(cleanedActive);
      }
    }
  }, [
    skipLocalFilterSave,
    activeFilters,
    flatFilterConfig.length,
    savedFilterConfig,
    setSavedFilterConfig,
    setSavedActiveFilters,
    validFilterIds,
    filterConfigById,
    setActiveFilters,
  ]);

  return {
    savedFilterConfig,
    setSavedFilterConfig,
    setSavedActiveFilters,
  };
};
